import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

// Error message display duration - gives users time to read the message.
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Success message display duration for screen readers.
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Fail-closed messages used when Shopify does not return a customer-facing error.
const GENERIC_ADD_TO_CART_ERROR = 'Unable to add this item to your cart. Please try again.';
const UNAVAILABLE_SELECTION_ERROR = 'This product selection is unavailable.';

/**
 * Shopify standard cart error code sourced directly from standard-events.d.ts.
 * @typedef {import('@shopify/events').CartErrorPayload['code']} CartErrorCode
 */

/**
 * Optional custom detail accepted by CartErrorEvent.
 * @typedef {import('@shopify/events').CartErrorPayloadDetail} CartErrorDetail
 */

/**
 * The subset of Shopify's AJAX cart response consumed by this component.
 *
 * @typedef {object} ShopifyCartResponse
 * @property {number | string | boolean} [status]
 * @property {string} [message]
 * @property {string} [description]
 * @property {unknown} [errors]
 * @property {unknown} [sections]
 */

/**
 * Converts Shopify's optional section-rendering response into the exact
 * cart-update detail shape used by this theme.
 *
 * @param {unknown} value
 * @returns {Record<string, string>[] | undefined}
 */
function normalizeCartSections(value) {
  const candidates = Array.isArray(value) ? value : value == null ? [] : [value];

  /** @type {Record<string, string>[]} */
  const normalizedSections = [];

  for (const candidate of candidates) {
    if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
      continue;
    }

    /** @type {Record<string, string>} */
    const normalizedSection = {};

    for (const [sectionId, sectionHtml] of Object.entries(candidate)) {
      if (typeof sectionHtml === 'string') {
        normalizedSection[sectionId] = sectionHtml;
      }
    }

    if (Object.keys(normalizedSection).length > 0) {
      normalizedSections.push(normalizedSection);
    }
  }

  return normalizedSections.length > 0 ? normalizedSections : undefined;
}

/**
 * Product-selection resource returned inside the theme's custom event detail.
 *
 * @typedef {object} ProductSelectionResource
 * @property {string | number} id
 * @property {boolean} [available]
 * @property {{preview_image?: {src?: string}}} [featured_media]
 */

/**
 * Theme-specific data carried in ProductSelectResult.detail.
 *
 * @typedef {object} ProductSelectionDetail
 * @property {Document | Element} html
 * @property {{id: string | number} | null} [newProduct]
 * @property {string | number | null} [productId]
 * @property {ProductSelectionResource | null} [resource]
 */

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isAbortError(error) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
}

/**
 * @param {unknown} error
 * @param {string} fallback
 * @returns {string}
 */
function getErrorMessage(error, fallback) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.trim()
  ) {
    return error.message;
  }

  return fallback;
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[]} */
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */
      (timeoutId) => clearTimeout(timeoutId),
    );

    this.#resetTimeouts = [];
    this.refs.addToCartButton.removeAttribute('data-added');

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   *
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) return;

    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));

    if (productForm?.isSubmitting) return;

    const quantitySelector = productForm?.refs?.quantitySelector;

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) return;
    }

    if (this.refs.addToCartButton.dataset.puppet === 'true') {
      return;
    }

    const eventTarget = event.target instanceof Element ? event.target : null;

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !eventTarget?.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }

    void this.animateAddToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly-to-cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) {
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    const flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);

    flyToCartElement.style.setProperty('background-image', `url(${image})`);

    flyToCartElement.style.setProperty('--start-opacity', '0');

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async () => {
    const { addToCartButton } = this.refs;

    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */
      (timeoutId) => clearTimeout(timeoutId),
    );

    this.#resetTimeouts = [];
    addToCartButton.dataset.added = 'true';

    try {
      await yieldToMainThread();
      await onAnimationEnd(addToCartButton);
    } catch (error) {
      if (!isAbortError(error)) {
        console.warn('[product-form] Add-to-cart animation did not complete:', error);
      }
    }

    if (!this.isConnected) {
      addToCartButton.removeAttribute('data-added');

      return;
    }

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index = this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * @typedef {{
 *   items: Array<{
 *     quantity: number,
 *     variant_id: number | string
 *   }>
 * }} Cart
 */

/**
 * @typedef {HTMLElement & {
 *   getValue?: () => number | string,
 *   setValue?: (value: number | string) => void,
 *   setCartQuantity?: (cartQuantity: number) => void,
 *   updateConstraints?: (
 *     min: string,
 *     max: string | null,
 *     step: string
 *   ) => void,
 *   canAddToCart?: () => {
 *     canAdd: boolean,
 *     maxQuantity?: number
 *   },
 *   element?: HTMLElement
 * }} QuantitySelector
 */

/**
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement | undefined} quantityLabelCartCount
 * @property {HTMLElement | undefined} quantityRules
 * @property {HTMLElement | undefined} productFormButtons
 * @property {HTMLElement | undefined} volumePricing
 * @property {QuantitySelector | undefined} quantitySelector
 * @property {HTMLElement | undefined} quantitySelectorWrapper
 * @property {HTMLElement | undefined} quantityLabel
 * @property {HTMLElement | undefined} pricePerItem
 */

/**
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity
 * @property {number} generation
 * @property {string | null} intendedVariantId
 * @property {Promise<unknown> | null} pendingVariantChange
 * @property {string | null} variantResolutionUrl
 */

/**
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 */

/**
 * A custom element that manages a product form.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {QueuedAddToCartItem[]} */
  #addToCartQueue = [];

  /** @type {Promise<unknown> | null} */
  #pendingVariantChange = null;

  /** @type {boolean} */
  #submissionInProgress = false;

  /** @type {boolean} */
  #queuedSubmissionPending = false;

  /** @type {AbortController | null} */
  #submissionAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;

    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        void this.#onProductSelect(/** @type {ProductSelectEvent} */ (event));
      },
      { signal },
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (event) => {
        this.#onCartUpdate(/** @type {CartLinesUpdateEvent} */ (event));
      },
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#submissionAbortController?.abort();
    this.#submissionAbortController = null;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    this.#timeout = undefined;
    this.#addToCartQueue = [];
    this.#pendingVariantChange = null;
    this.#variantChangeInProgress = false;
    this.#queuedSubmissionPending = false;

    this.#setSubmissionState(false);
  }

  get isSubmitting() {
    return this.#submissionInProgress || this.#queuedSubmissionPending;
  }

  #getVariantIdInput() {
    const value = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;

    return this.#normalizeVariantId(value);
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');

      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      return response.json();
    });
  }

  /**
   * @param {Cart} cart
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();

    if (!variantIdInput) return;

    const cartItem = cart.items.find((item) => String(item.variant_id) === variantIdInput);

    const cartQty = cartItem ? cartItem.quantity : 0;

    /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */
    const quantitySelector = this.querySelector('quantity-selector-component');

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    this.#updateQuantityLabel(cartQty);
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (!this.#getVariantIdInput()) {
      return;
    }

    if (!event.promise) {
      void this.#refreshCart()
        .then((cart) => {
          this.#updateCartQuantity(cart);
        })
        .catch((error) => {
          if (!isAbortError(error)) {
            console.warn('[product-form] Unable to refresh cart state:', error);
          }
        });

      return;
    }

    event.promise
      .then(({ detail }) => {
        if (detail?.sourceId === this.id) {
          return;
        }

        if (detail?.items) {
          this.#updateCartQuantity(
            /** @type {Cart} */ ({
              items: detail.items,
            }),
          );

          return;
        }

        return this.#refreshCart().then((cart) => {
          this.#updateCartQuantity(cart);
        });
      })
      .catch((error) => {
        if (!isAbortError(error)) {
          console.warn('[product-form] Event promise rejected:', error);
        }
      });
  };

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();

    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));

    if (!form || !form.checkValidity()) {
      form?.reportValidity();
      return;
    }

    if (this.isSubmitting) return;

    if (this.#variantChangeInProgress) {
      this.#queuedSubmissionPending = true;

      this.#addToCartQueue = [this.#createQueuedAddToCartItem()];

      void this.refs.addToCartButtonContainer?.animateAddToCart?.();

      return;
    }

    void this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * @returns {number}
   */
  #getQuantity() {
    const requestedQuantity = this.refs.quantitySelector?.getValue?.();

    return this.#normalizeQuantity(requestedQuantity, this.dataset.quantityDefault) ?? 1;
  }

  /**
   * @returns {QueuedAddToCartItem}
   */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();

    const selectedOption = picker?.selectedOption;

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#submissionInProgress) {
      return;
    }

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs?.addToCartButton?.disabled,
      );

      if (anyButtonDisabled) return;
    }

    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));

    if (!form) {
      this.#reportError(GENERIC_ADD_TO_CART_ERROR);

      return;
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        const errorTemplate = this.dataset.quantityErrorMax || GENERIC_ADD_TO_CART_ERROR;

        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');

        this.#reportError(errorMessage);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }

    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const variantId = this.#normalizeVariantId(formData.get('id'));

    const itemCount = this.#normalizeQuantity(formData.get('quantity'), this.dataset.quantityDefault);

    if (!variantId || itemCount === null) {
      this.#reportError(UNAVAILABLE_SELECTION_ERROR);

      this.#dispatchCartError(UNAVAILABLE_SELECTION_ERROR, 'INVALID');

      return;
    }

    formData.set('id', variantId);

    formData.set('quantity', itemCount.toString());

    const sectionIds = this.#getCartSectionIds();

    if (sectionIds.length > 0) {
      formData.set('sections', sectionIds.join(','));

      formData.set('sections_url', window.location.pathname);
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: variantId,
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      }),
    );

    const requestController = new AbortController();

    this.#submissionAbortController = requestController;

    this.#setSubmissionState(true);

    try {
      const fetchCfg = fetchConfig('javascript', {
        body: formData,
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        credentials: 'same-origin',
        signal: requestController.signal,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      const responseData = await this.#readJsonResponse(response);

      if (!response.ok || responseData.status) {
        const message = responseData.message || responseData.description || GENERIC_ADD_TO_CART_ERROR;

        this.#dispatchCartError(message, 'INVALID', {
          description: responseData.description,
          error: responseData.errors,
        });

        this.#reportError(message);

        try {
          const ajaxCart = await this.#refreshCart();

          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: {
              didError: true,
              items: ajaxCart.items,
              source: 'product-form-component',
              sourceId: this.id.toString(),
              itemCount,
              productId: this.dataset.productId,
            },
          });
        } catch (refreshError) {
          deferredEventPromise.reject(refreshError);
        }

        return;
      }

      this.#hideError();

      this.#announceAdded(allAddToCartContainers);

      try {
        const ajaxCart = await this.#refreshCart();

        const renderedSections = /** @type {Record<string, string>[] | undefined} */ (
          normalizeCartSections(responseData.sections)
        );

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount,
            productId: this.dataset.productId,
            ...(renderedSections
              ? {
                  sections: renderedSections,
                }
              : {}),
            didError: false,
          },
        });

        if (this.#getVariantIdInput()) {
          this.#updateCartQuantity(ajaxCart);
        }
      } catch (refreshError) {
        deferredEventPromise.reject(refreshError);

        console.warn('[product-form] Item was added, but cart state could not be refreshed:', refreshError);
      }
    } catch (error) {
      deferredEventPromise.reject(error);

      if (!isAbortError(error)) {
        const message = getErrorMessage(error, GENERIC_ADD_TO_CART_ERROR);

        console.error('[product-form] Add to cart failed:', error);

        this.#dispatchCartError(message, 'SERVICE_UNAVAILABLE');

        this.#reportError(message);
      }
    } finally {
      if (this.#submissionAbortController === requestController) {
        this.#submissionAbortController = null;
      }

      this.#setSubmissionState(false);

      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    }
  }

  /**
   * @param {Array<{
   *   variantId: string,
   *   quantity: number
   * }>} items
   */
  async #processBatchAddToCart(items) {
    if (this.#submissionInProgress) {
      return;
    }

    /** @type {Map<string, number>} */
    const mergedItems = new Map();

    for (const item of items) {
      const variantId = this.#normalizeVariantId(item.variantId);

      const quantity = this.#normalizeQuantity(item.quantity, 1);

      if (!variantId || quantity === null) {
        continue;
      }

      mergedItems.set(variantId, (mergedItems.get(variantId) || 0) + quantity);
    }

    const normalizedItems = Array.from(mergedItems, ([variantId, quantity]) => ({
      variantId,
      quantity,
    }));

    if (normalizedItems.length === 0) {
      this.#reportError(UNAVAILABLE_SELECTION_ERROR);

      return;
    }

    const totalQuantity = normalizedItems.reduce((sum, item) => sum + item.quantity, 0);

    const sectionIds = this.#getCartSectionIds();

    const payload = {
      items: normalizedItems.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      ...(sectionIds.length > 0
        ? {
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }
        : {}),
    };

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: normalizedItems.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      }),
    );

    const requestController = new AbortController();

    this.#submissionAbortController = requestController;

    this.#setSubmissionState(true);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        signal: requestController.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await this.#readJsonResponse(response);

      if (!response.ok || responseData.status) {
        const message = responseData.message || responseData.description || GENERIC_ADD_TO_CART_ERROR;

        this.#dispatchCartError(message, 'INVALID', {
          description: responseData.description,
          error: responseData.errors,
        });

        this.#reportError(message);

        try {
          const ajaxCart = await this.#refreshCart();

          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: {
              didError: true,
              items: ajaxCart.items,
              source: 'product-form-component',
              sourceId: this.id.toString(),
              itemCount: totalQuantity,
              productId: this.dataset.productId,
            },
          });
        } catch (refreshError) {
          deferredEventPromise.reject(refreshError);
        }

        return;
      }

      this.#hideError();

      const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll('add-to-cart-component')
      );

      this.#announceAdded(allAddToCartContainers);

      try {
        const ajaxCart = await this.#refreshCart();

        const renderedSections = /** @type {Record<string, string>[] | undefined} */ (
          normalizeCartSections(responseData.sections)
        );

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            ...(renderedSections
              ? {
                  sections: renderedSections,
                }
              : {}),
            didError: false,
          },
        });

        this.#updateCartQuantity(ajaxCart);
      } catch (refreshError) {
        deferredEventPromise.reject(refreshError);

        console.warn('[product-form] Items were added, but cart state could not be refreshed:', refreshError);
      }
    } catch (error) {
      deferredEventPromise.reject(error);

      if (!isAbortError(error)) {
        const message = getErrorMessage(error, GENERIC_ADD_TO_CART_ERROR);

        console.error('[product-form] Batch add to cart failed:', error);

        this.#dispatchCartError(message, 'SERVICE_UNAVAILABLE');

        this.#reportError(message);
      }
    } finally {
      if (this.#submissionAbortController === requestController) {
        this.#submissionAbortController = null;
      }

      this.#setSubmissionState(false);
    }
  }

  /**
   * @param {number} cartQty
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;

    if (!quantityLabel) return;

    const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);

    if (inCartText && inCartText[2]) {
      quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
    }

    quantityLabel.classList.toggle('hidden', cartQty === 0);
  }

  /**
   * @param {string} text
   */
  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  /**
   * @param {FormDataEntryValue | string | number | null | undefined} value
   * @returns {string | null}
   */
  #normalizeVariantId(value) {
    const variantId = String(value ?? '').trim();

    if (!/^\d+$/.test(variantId) || variantId === '0') {
      return null;
    }

    return variantId;
  }

  /**
   * @param {FormDataEntryValue | string | number | null | undefined} value
   * @param {FormDataEntryValue | string | number | null | undefined} fallback
   * @returns {number | null}
   */
  #normalizeQuantity(value, fallback) {
    const requested = Number(value);
    const fallbackValue = Number(fallback);

    const quantity =
      Number.isFinite(requested) && requested > 0
        ? requested
        : Number.isFinite(fallbackValue) && fallbackValue > 0
          ? fallbackValue
          : 1;

    if (!Number.isInteger(quantity) || quantity < 1) {
      return null;
    }

    return quantity;
  }

  /**
   * @returns {string[]}
   */
  #getCartSectionIds() {
    /** @type {Set<string>} */
    const sectionIds = new Set();

    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    }

    return Array.from(sectionIds);
  }

  /**
   * @param {Response} response
   * @returns {Promise<ShopifyCartResponse>}
   */
  async #readJsonResponse(response) {
    let responseData;

    try {
      responseData = await response.json();
    } catch {
      throw new Error(`Shopify returned an invalid cart response (${response.status}).`);
    }

    if (typeof responseData !== 'object' || responseData === null || Array.isArray(responseData)) {
      throw new Error(`Shopify returned an invalid cart response (${response.status}).`);
    }

    return /** @type {ShopifyCartResponse} */ (responseData);
  }

  /**
   * @param {string} message
   * @param {CartErrorCode} code
   * @param {CartErrorDetail} [detail]
   */
  #dispatchCartError(message, code, detail) {
    this.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code,
        ...(detail ? { detail } : {}),
      }),
    );
  }

  /**
   * @param {NodeListOf<AddToCartComponent>} containers
   */
  #announceAdded(containers) {
    const anyAddToCartButton = containers[0]?.refs?.addToCartButton;

    if (!anyAddToCartButton) {
      return;
    }

    const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');

    const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added || 'Added to cart';

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    this.#setLiveRegionText(addedText);

    this.#timeout = setTimeout(() => {
      this.#clearLiveRegionText();
      this.#timeout = undefined;
    }, SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  #hideError() {
    const { addToCartTextError } = this.refs;

    addToCartTextError?.classList.add('hidden');

    addToCartTextError?.removeAttribute('aria-live');

    const messageElement = addToCartTextError?.querySelector('[data-product-form-error-message]');

    if (messageElement) {
      messageElement.textContent = '';
    }
  }

  /**
   * @param {boolean} isSubmitting
   */
  #setSubmissionState(isSubmitting) {
    this.#submissionInProgress = isSubmitting;

    this.toggleAttribute('data-submitting', isSubmitting);

    this.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
  }

  /**
   * @param {string} message
   */
  #reportError(message) {
    const { addToCartTextError } = this.refs;

    const safeMessage = message?.trim() || GENERIC_ADD_TO_CART_ERROR;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');

      let messageElement = addToCartTextError.querySelector('[data-product-form-error-message]');

      if (!messageElement) {
        messageElement = document.createElement('span');

        messageElement.setAttribute('data-product-form-error-message', '');

        addToCartTextError.appendChild(messageElement);
      }

      messageElement.textContent = safeMessage;
    }

    this.#setLiveRegionText(safeMessage);

    this.#timeout = setTimeout(() => {
      addToCartTextError?.classList.add('hidden');

      this.#clearLiveRegionText();
      this.#timeout = undefined;
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * @param {Element | null | undefined} currentElement
   * @param {Element | null | undefined} newElement
   * @param {Element | null} [insertReferenceElement]
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');

    if (sourceCard && !sourceCard.contains(this)) {
      return;
    }

    const generation = ++this.#variantChangeGeneration;

    this.#variantChangeInProgress = true;
    this.#pendingVariantChange = event.promise;

    try {
      const result = await event.promise;

      const detail = /** @type {ProductSelectionDetail | undefined} */ (result.detail);

      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      if (newProduct?.id != null) {
        this.dataset.productId = String(newProduct.id);
      } else if (productId != null && String(productId) !== this.dataset.productId) {
        return;
      }

      this.refs.variantId.value = resource?.id != null ? String(resource.id) : '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;

      const currentAddToCartButton = currentAddToCartButtonContainer?.refs?.addToCartButton;

      if (!currentAddToCartButton && !acceleratedCheckoutButtonContainer) {
        return;
      }

      if (currentAddToCartButtonContainer) {
        if (resource == null || resource.available === false) {
          currentAddToCartButtonContainer.disable();
        } else {
          currentAddToCartButtonContainer.enable();
        }
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');

      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available === false) {
          acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer.removeAttribute('hidden');
        }
      }

      const productVariantMedia = resource?.featured_media?.preview_image?.src;

      if (productVariantMedia) {
        const separator = productVariantMedia.includes('?') ? '&' : '?';

        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          `${productVariantMedia}${separator}width=100`,
        );
      } else {
        this.refs.addToCartButtonContainer?.removeAttribute('data-product-variant-media');
      }

      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);

        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');

      const isQuantityRulesChanging = Boolean(quantityRules) !== Boolean(newQuantityRules);

      const newPricePerItem = html.querySelector('price-per-item');

      const isPricePerItemChanging = Boolean(pricePerItem) !== Boolean(newPricePerItem);

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            this.refs.quantitySelector.setValue?.(currentQuantityValue);

            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step,
            );

            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        /** @type {Array<[
         *   string,
         *   HTMLElement | undefined,
         *   HTMLElement | undefined
         * ]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      const currentVolumePricing = this.refs.volumePricing;

      const newVolumePricing = html.querySelector('volume-pricing');

      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      void this.#refreshCart()
        .then((cart) => {
          this.#updateCartQuantity(cart);
        })
        .catch((error) => {
          if (!isAbortError(error)) {
            console.warn('[product-form] Unable to refresh variant cart quantity:', error);
          }
        });
    } catch (error) {
      if (!isAbortError(error) && generation === this.#variantChangeGeneration) {
        console.warn('[product-form] Variant selection could not be resolved:', error);

        this.refs.variantId.value = '';

        this.refs.addToCartButtonContainer?.disable();

        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');

        this.#reportError(UNAVAILABLE_SELECTION_ERROR);
      }
    } finally {
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        this.#pendingVariantChange = null;

        await this.#drainAddToCartQueue();
      }
    }
  };

  async #drainAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) {
      this.#queuedSubmissionPending = false;

      return;
    }

    const queuedItems = [...this.#addToCartQueue];

    this.#addToCartQueue = [];

    /** @type {Array<{
     *   variantId: string,
     *   quantity: number
     * }>} */
    const resolvedItems = [];

    for (const item of queuedItems) {
      const resolvedItem = await this.#resolveQueuedAddToCartItem(item).catch(() => null);

      if (resolvedItem) {
        resolvedItems.push(resolvedItem);
      }
    }

    this.#queuedSubmissionPending = false;

    if (resolvedItems.length === 0) {
      this.#reportError(UNAVAILABLE_SELECTION_ERROR);

      return;
    }

    void this.#processBatchAddToCart(resolvedItems);
  }

  /**
   * @param {QueuedAddToCartItem} item
   *
   * @returns {Promise<{
   *   variantId: string,
   *   quantity: number
   * } | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);

    if (!variantId) return null;

    const quantity = this.#normalizeQueuedQuantity(item.quantity, quantityConstraints);

    if (quantity === null) {
      return null;
    }

    return {
      variantId,
      quantity,
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   *
   * @returns {Promise<{
   *   variantId: string | null,
   *   quantityConstraints:
   *     QuantityConstraints | null
   * }>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;

    /** @type {boolean | undefined} */
    let available;

    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (item.pendingVariantChange) {
      try {
        const result =
          /** @type {{
           *   detail?: {
           *     resource?: ProductSelectionResource | null,
           *     html?: Document | Element
           *   }
           * }} */ (await item.pendingVariantChange);

        const resource = result.detail?.resource;

        quantityConstraints = this.#getQuantityConstraintsFromHtml(result.detail?.html);

        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;

          available = resource.available !== false;
        }
      } catch {
        const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
          variantId: null,
          available: false,
          quantityConstraints: null,
        }));

        resolvedVariantId = resolvedVariant.variantId;

        available = resolvedVariant.available;

        quantityConstraints = resolvedVariant.quantityConstraints;
      }
    }

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;

    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? (this.#getVariantIdInput() ?? null) : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return {
      variantId,
      quantityConstraints,
    };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   *
   * @returns {number | null}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    const normalizedRequestedQuantity = this.#normalizeQuantity(quantity, 1);

    if (normalizedRequestedQuantity === null) {
      return null;
    }

    if (!quantityConstraints) {
      return normalizedRequestedQuantity;
    }

    const min = Math.max(parseIntOrDefault(quantityConstraints.min, 1) ?? 1, 1);

    const max = parseIntOrDefault(quantityConstraints.max, null);

    const step = Math.max(parseIntOrDefault(quantityConstraints.step, 1) ?? 1, 1);

    const cartQuantity = Math.max(parseIntOrDefault(quantityConstraints.cartQuantity, 0) ?? 0, 0);

    const remainingQuantity = max === null ? null : max - cartQuantity;

    if (remainingQuantity !== null && remainingQuantity < min) {
      return null;
    }

    let normalizedQuantity = normalizedRequestedQuantity;

    if ((normalizedQuantity - min) % step !== 0) {
      normalizedQuantity = min + Math.floor((normalizedQuantity - min) / step) * step;
    }

    normalizedQuantity = Math.max(min, normalizedQuantity);

    if (remainingQuantity !== null) {
      normalizedQuantity = Math.min(remainingQuantity, normalizedQuantity);
    }

    return normalizedQuantity >= min ? normalizedQuantity : null;
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );

    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /**
   * @param {string | null} variantResolutionUrl
   *
   * @returns {Promise<{
   *   variantId: string | null,
   *   available:
   *     boolean | undefined,
   *   quantityConstraints:
   *     QuantityConstraints | null
   * }>}
   */
  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) {
      return {
        variantId: null,
        available: undefined,
        quantityConstraints: null,
      };
    }

    const response = await fetch(variantResolutionUrl, {
      credentials: 'same-origin',
    });

    if (!response.ok) {
      return {
        variantId: null,
        available: false,
        quantityConstraints: null,
      };
    }

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);

    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;

    if (!textContent) {
      return {
        variantId: null,
        available: false,
        quantityConstraints,
      };
    }

    try {
      const resource = JSON.parse(textContent);

      if (!resource || typeof resource !== 'object') {
        return {
          variantId: null,
          available: false,
          quantityConstraints,
        };
      }

      return {
        variantId: resource.id != null ? String(resource.id) : null,
        available: resource.available !== false,
        quantityConstraints,
      };
    } catch {
      return {
        variantId: null,
        available: false,
        quantityConstraints,
      };
    }
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');

    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );

    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);

      if (matchingPicker) {
        return matchingPicker;
      }
    }

    return pickers.length === 1 ? (pickers[0] ?? null) : null;
  }

  /**
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));

    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
